/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Tariffs And Services
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.4.0
 */

import { Fragment, useEffect, useRef, useState } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '@components/common/Title/Title';
import withAsyncStateManagement from '@modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { Loader } from '@modules/loader/components/Loader';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';
import { icons } from '@theme/index';

import ErrorPage from '../../ErrorPage/ErrorPage';
import { productsSagas } from '../productsSagas';
import { initTariffsAndServicesAction } from '../tariffsAndServicesActions';
import { EProductsPageFrom, EProductsTab } from '../tariffsAndServicesConstants';
import {
    makeSelectProductCategoryListIsError,
    makeSelectProductCategoryListIsLoading,
    makeSelectProductsRegionIsError,
    makeSelectProductsTabSelected,
    makeSelectTariffsAndServicesIsFooterVisible,
    makeSelectTariffsAndServicesProductOfferingsAvailableIsError,
    makeSelectTariffsAndServicesProductOfferingsConnectedIsError,
    makeSelectTariffsAndServicesProductOfferingsErrorText,
    makeSelectTariffsAndServicesProductOfferingsIsLoading,
} from '../tariffsAndServicesSelectors';
import { IInitTariffsAndServicesActionPayload } from '../tariffsAndServicesTypes';
import PageProductsTitle from './PageProductsTitle';
import ProductsContent from './ProductsContent';
import ProductsFooter from './ProductsFooter';
import ProductsSideMenu from './ProductsSideMenu';
import {
    PageTariffsAndServicesBodyStyled,
    PageTariffsAndServicesContentStyled,
    PageTariffsAndServicesSideStyled,
    PageTariffsAndServicesStyled,
} from './styled/PageTariffsAndServicesStyled';

interface IPageTariffsAndServicesProps {
    pageFrom: EProductsPageFrom;
}

interface IMatchProps {
    selectedCategory: string;
}

export type TPageTariffsAndServices = IPageTariffsAndServicesProps &
    IConnectedProps &
    IDispatchProps &
    RouteComponentProps<IMatchProps>;

export const PageProducts = (props: TPageTariffsAndServices) => {
    const {
        initTariffsAndServicesAction,
        pageFrom,

        categoryListIsError,
        categoryListIsLoading,
        productOfferingsAvailableIsError,
        productOfferingsConnectedIsError,
        productOfferingsErrorText,
        productOfferingsIsLoading,
        productsTranslation,
        regionIsError,
        tabSelected,
    } = props;

    const [isError, setError] = useState(false);
    const { clientId } = useParams<IRouteParams>();

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        initTariffsAndServicesAction({ clientId, pageFrom });
    }, [clientId, pageFrom, initTariffsAndServicesAction]);

    useEffect(() => {
        setError(
            (tabSelected === EProductsTab.AVAILABLE && productOfferingsAvailableIsError) ||
                (tabSelected === EProductsTab.CONNECTED && productOfferingsConnectedIsError) ||
                categoryListIsError ||
                regionIsError,
        );
    }, [
        categoryListIsError,
        productOfferingsAvailableIsError,
        productOfferingsConnectedIsError,
        regionIsError,
        tabSelected,
    ]);

    return (
        <PageTariffsAndServicesStyled>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageProducts]} />

            <Title pageName={ECommonTranslationPages.products} />

            <Loader showLoader={categoryListIsLoading}>
                <PageProductsTitle />

                {isError && !productOfferingsIsLoading ? (
                    <ErrorPage
                        src={icons.errorDeliveryImage3x}
                        text={productOfferingsErrorText}
                        title={productsTranslation.errorTitle}
                    />
                ) : (
                    <Fragment>
                        <PageTariffsAndServicesBodyStyled>
                            <PageTariffsAndServicesSideStyled>
                                <ProductsSideMenu />
                            </PageTariffsAndServicesSideStyled>

                            <PageTariffsAndServicesContentStyled ref={contentRef}>
                                <Loader showLoader={productOfferingsIsLoading} />
                                <ProductsContent />
                            </PageTariffsAndServicesContentStyled>
                        </PageTariffsAndServicesBodyStyled>

                        {props.isFooterVisible && <ProductsFooter />}
                    </Fragment>
                )}
            </Loader>
        </PageTariffsAndServicesStyled>
    );
};

interface IConnectedProps {
    categoryListIsError: boolean;
    categoryListIsLoading: boolean;
    isFooterVisible: boolean;
    productOfferingsAvailableIsError: boolean;
    productOfferingsConnectedIsError: boolean;
    productOfferingsErrorText: string;
    productOfferingsIsLoading: boolean;
    productsTranslation: IPageProductsTranslation;
    regionIsError: boolean;
    tabSelected: EProductsTab;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    categoryListIsError: makeSelectProductCategoryListIsError(),
    categoryListIsLoading: makeSelectProductCategoryListIsLoading(),
    isFooterVisible: makeSelectTariffsAndServicesIsFooterVisible(),
    productOfferingsAvailableIsError: makeSelectTariffsAndServicesProductOfferingsAvailableIsError(),
    productOfferingsConnectedIsError: makeSelectTariffsAndServicesProductOfferingsConnectedIsError(),
    productOfferingsErrorText: makeSelectTariffsAndServicesProductOfferingsErrorText(),
    productOfferingsIsLoading: makeSelectTariffsAndServicesProductOfferingsIsLoading(),
    productsTranslation: makeSelectTranslationPageProducts(),
    regionIsError: makeSelectProductsRegionIsError(),
    tabSelected: makeSelectProductsTabSelected(),
});

interface IDispatchProps {
    initTariffsAndServicesAction: (payload: IInitTariffsAndServicesActionPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    initTariffsAndServicesAction: (payload) => dispatch(initTariffsAndServicesAction(payload)),
});

export default withAsyncStateManagement(connect(mapStateToProps, mapDispatchToProps)(PageProducts), {
    sagas: productsSagas,
});
